import { CircleLoading } from "@layer/icons";
import { cn } from "@layer/utils/cn";
import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";
import type * as React from "react";

const buttonVariants = cva(
	"leading-none! inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-sm text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:shadow-[0_0_0_3px_var(--app-focus-ring)]",
	{
		variants: {
			variant: {
				default:
					"bg-app-bg-inverse text-app-text-inverse shadow-sm hover:bg-app-bg-inverse/90 active:bg-app-bg-inverse/80",
				primary:
					"bg-button-primary-bg text-button-primary-text shadow-sm hover:bg-button-primary-bg-hover active:bg-button-primary-bg-active",
				destructive:
					"bg-danger-9 text-white shadow-sm hover:bg-danger-10 active:bg-danger-11",
				outline:
					"border border-button-secondary-border bg-button-secondary-bg text-button-secondary-text hover:bg-button-secondary-bg-hover",
				secondary:
					"border border-button-secondary-border bg-button-secondary-bg text-button-secondary-text hover:bg-button-secondary-bg-hover",
				ghost: "text-button-ghost-text hover:bg-button-ghost-bg-hover",
				link: "text-link underline-offset-4 hover:underline hover:text-link-hover",
			},
			size: {
				default: "leading-none px-6! py-2.5! has-[>svg]:px-3 ",
				xs: "h-6 gap-1 rounded-sm px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
				sm: "h-8 rounded-sm gap-1.5 px-3 has-[>svg]:px-2.5",
				lg: "h-10 rounded-sm px-6 has-[>svg]:px-4",
				tableAction: "size-6",
				icon: "size-9",
				"icon-xs":
					"size-6 rounded-sm [&_svg:not([class*='size-'])]:size-3",
				"icon-sm": "size-8",
				"icon-lg": "size-10",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
		},
	},
);

function Button({
	className,
	variant = "default",
	size = "default",
	asChild = false,
	loading = false,
	...props
}: React.ComponentProps<"button"> &
	VariantProps<typeof buttonVariants> & {
		asChild?: boolean;
		loading?: boolean;
	}) {
	const Comp = asChild ? Slot.Root : "button";

	return (
		<Comp
			data-slot="button"
			data-variant={variant}
			data-size={size}
			className={cn(buttonVariants({ variant, size, className }))}
			disabled={!!loading}
			{...props}
		>
			{loading ? (
				<CircleLoading variant="duocolor" className="animate-spin" />
			) : (
				props.children
			)}
		</Comp>
	);
}

export { Button, buttonVariants };
